import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { Printer, ArrowLeft, Download, FileImage } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import { exportReceiptPDF, exportReceiptPNG } from '../../utils/pdfExport'
import { useAuthStore } from '../../store/authStore'
import ReceiptDocument from '../../components/finance/ReceiptDocument'

export default function Receipt() {
  const { paymentId } = useParams()
  const { user } = useAuthStore()
  const receiptRef = useRef(null)
  const [receipt, setReceipt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null) // 'pdf' | 'png' | null
  const [error, setError] = useState(null)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    fetchReceipt()
  }, [paymentId])

  // Track viewport width so the receipt can stack on phones.
  // Print/export always uses the same shared component regardless of this flag.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)')
    setIsMobile(mql.matches)
    const onChange = (e) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const fetchReceipt = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get(`/finance/receipts/${paymentId}`)
      setReceipt(data.receipt)
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load receipt'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPDF = async () => {
    if (!receipt) return
    setDownloading('pdf')
    try {
      await exportReceiptPDF(receipt, { element: receiptRef.current })
      toast.success('Receipt PDF downloaded')
    } catch (err) {
      console.error('Download receipt PDF error:', err)
      toast.error('Failed to generate receipt PDF')
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadPNG = async () => {
    if (!receipt) return
    setDownloading('png')
    try {
      await exportReceiptPNG(receipt, { element: receiptRef.current })
      toast.success('Receipt PNG downloaded')
    } catch (err) {
      console.error('Download receipt PNG error:', err)
      toast.error('Failed to generate receipt PNG')
    } finally {
      setDownloading(null)
    }
  }

  const backUrl =
    user?.user_type === 'admin'
      ? '/admin/finance'
      : user?.user_type === 'finance'
        ? '/finance/dashboard'
        : '/student/payments'

  if (loading) return <LoadingSpinner label="Loading receipt" />

  if (error) {
    return (
      <div className="cinema-empty">
        <h3 className="font-display text-base text-white font-light mb-2">Receipt Unavailable</h3>
        <p className="font-sans text-xs text-cinema-muted mb-4">{error}</p>
        <Link
          to={backUrl}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans font-medium cinema-btn-primary"
        >
          <ArrowLeft size={13} /> Back
        </Link>
      </div>
    )
  }

  if (!receipt) return null

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Action bar — hidden when printing */}
      <div className="flex items-center justify-between no-print gap-2">
        <Link
          to={backUrl}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans font-medium cinema-glass border border-white/10 text-cinema-muted hover:text-white transition-colors"
        >
          <ArrowLeft size={13} /> Back
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadPDF}
            disabled={!!downloading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-3.5 sm:py-2 text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
          >
            <Download size={13} className={downloading === 'pdf' ? 'animate-bounce' : ''} />
            <span className="hidden sm:inline">Download PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button
            onClick={handleDownloadPNG}
            disabled={!!downloading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-3.5 sm:py-2 text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
          >
            <FileImage size={13} className={downloading === 'png' ? 'animate-bounce' : ''} />
            <span className="hidden sm:inline">Download PNG</span>
            <span className="sm:hidden">PNG</span>
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-xs font-sans font-medium cinema-btn-primary"
          >
            <Printer size={13} /> <span className="hidden sm:inline">Print / Save as PDF</span>
            <span className="sm:hidden">Print</span>
          </button>
        </div>
      </div>

      {/* Receipt document — the single shared component, also captured for export */}
      <ReceiptDocument ref={receiptRef} receipt={receipt} isMobile={isMobile} />
    </div>
  )
}
